'use client';

import Link from 'next/link';
import type { OpdsEntry, LibraryConnection } from '@/lib/opds/types';
import { proxyImageUrl } from '@/lib/utils/fetch';
import { encodeFeedPath } from '@/lib/utils/url';

type NavTileKind = 'work' | 'sequence' | 'psequence' | 'other';

function classifyNavTile(entry: OpdsEntry, href: string | undefined): NavTileKind {
  // Entry ids carry an explicit type tag from the v2 server
  // (work:, work-title:, sequence:, psequence:). Author-scoped sequence
  // hrefs like /author/X/Серии/.../<seq> can't be detected from the
  // path alone — the id is the reliable signal.
  const id = entry.id || '';
  if (id.includes(':work:') || id.includes(':work-title:')) return 'work';
  if (id.includes(':psequence:')) return 'psequence';
  if (id.includes(':sequence:')) return 'sequence';

  if (href) {
    try {
      const path = new URL(href, 'http://x').pathname;
      if (path.startsWith('/work/')) return 'work';
      if (path.startsWith('/psequence/')) return 'psequence';
      if (path.startsWith('/sequence/')) return 'sequence';
    } catch {
      /* fallthrough */
    }
  }
  return 'other';
}

function BookCoverFallback({ title }: { title: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-200 p-4 text-center">
      <svg className="h-20 w-20 text-emerald-600" viewBox="0 0 24 24" aria-hidden>
        {/* Back cover (slight depth) */}
        <rect x="7" y="3.5" width="12" height="17" rx="1.2" fill="currentColor" opacity="0.35" />
        {/* Front cover */}
        <rect x="5" y="3" width="12" height="17" rx="1.2" fill="currentColor" />
        {/* Spine highlight */}
        <rect x="5" y="3" width="2.2" height="17" rx="1.2" fill="currentColor" opacity="0.7" />
        {/* Title-line decoration */}
        <path d="M9.5 8h6M9.5 11h6M9.5 14h4" stroke="white" strokeOpacity="0.85" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
      <span className="line-clamp-3 text-sm font-medium leading-tight text-neutral-700">
        {title}
      </span>
    </div>
  );
}

function NavTilePlaceholder({ title, kind }: { title: string; kind: NavTileKind }) {
  // Strip a leading sequence-position prefix ("0001 ", "0005 ") and a
  // trailing "(N)" count — the body title below the visual carries both.
  const cleanTitle = title
    .replace(/^\d+\s+/, '')
    .replace(/\s*\(\d+\)\s*$/, '')
    .trim();

  const palette: Record<NavTileKind, { bg: string; icon: string; label: string; labelText: string }> = {
    work: {
      bg: 'from-violet-50 via-violet-100 to-violet-200',
      icon: 'text-violet-500',
      label: 'text-violet-700',
      labelText: 'Произведение',
    },
    sequence: {
      bg: 'from-amber-50 via-amber-100 to-amber-200',
      icon: 'text-amber-500',
      label: 'text-amber-800',
      labelText: 'Серия',
    },
    psequence: {
      bg: 'from-sky-50 via-sky-100 to-sky-200',
      icon: 'text-sky-500',
      label: 'text-sky-700',
      labelText: 'Изд. серия',
    },
    other: {
      bg: 'from-neutral-50 to-neutral-200',
      icon: 'text-neutral-400',
      label: 'text-neutral-500',
      labelText: '',
    },
  };
  const c = palette[kind];

  return (
    <div className={`flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br p-4 text-center ${c.bg}`}>
      {c.labelText && (
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${c.label}`}>
          {c.labelText}
        </span>
      )}
      <NavTileIcon kind={kind} className={`h-20 w-20 ${c.icon}`} />
      <span className="line-clamp-3 text-sm font-medium leading-tight text-neutral-700">
        {cleanTitle}
      </span>
    </div>
  );
}

function NavTileIcon({ kind, className }: { kind: NavTileKind; className?: string }) {
  if (kind === 'work') {
    // Three offset stacked pages — "editions" stacked together.
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden>
        <rect x="3"  y="7" width="13" height="15" rx="1.5" fill="currentColor" opacity="0.35" />
        <rect x="5.5" y="5" width="13" height="15" rx="1.5" fill="currentColor" opacity="0.6" />
        <rect x="8"  y="3" width="13" height="15" rx="1.5" fill="currentColor" />
        <path d="M11 7h7M11 10h7M11 13h5" stroke="white" strokeOpacity="0.7" strokeWidth="1" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'psequence') {
    // Book spines with a "P" mark on the middle one.
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden>
        <rect x="3"  y="4" width="4.2" height="16" rx="0.7" fill="currentColor" opacity="0.45" />
        <rect x="9"  y="4" width="4.2" height="16" rx="0.7" fill="currentColor" opacity="0.7" />
        <rect x="15" y="4" width="4.2" height="16" rx="0.7" fill="currentColor" />
        <text x="11.1" y="14" fontSize="4.6" fontWeight="800" textAnchor="middle" fill="white" stroke="none">P</text>
      </svg>
    );
  }
  if (kind === 'sequence') {
    // Three book spines with varying opacity for depth.
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden>
        <rect x="3"  y="4" width="4.2" height="16" rx="0.7" fill="currentColor" opacity="0.45" />
        <rect x="9"  y="4" width="4.2" height="16" rx="0.7" fill="currentColor" opacity="0.7" />
        <rect x="15" y="4" width="4.2" height="16" rx="0.7" fill="currentColor" />
      </svg>
    );
  }
  // Fallback — generic list icon
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 6h16M4 12h16M4 18h10" />
    </svg>
  );
}

export function EntryCard({
  entry,
  library,
  isNavigation = false,
  feedUrl,
  entryIndex,
}: {
  entry: OpdsEntry;
  library: LibraryConnection;
  isNavigation?: boolean;
  feedUrl?: string;
  entryIndex?: number;
}) {
  const subsectionLink = entry.links.find(
    (l) => l.type?.includes('opds-catalog') || l.rel === 'subsection' || l.rel === 'http://opds-spec.org/sort/new' || l.rel === 'http://opds-spec.org/sort/popular'
  );

  if (isNavigation && subsectionLink) {
    return (
      <Link
        href={`/library/${library.id}/feed/${encodeFeedPath(subsectionLink.href)}`}
        className="flex items-center gap-4 rounded-xl border border-neutral-200 bg-white p-4 py-5 transition-all hover:border-blue-200 hover:shadow-md sm:py-4"
      >
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-neutral-500 sm:h-10 sm:w-10">
          <svg className="h-6 w-6 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-medium text-neutral-900 sm:text-sm">{entry.title}</h3>
          {(entry.content?.value || entry.summary) && (
            <p className="mt-0.5 truncate text-sm text-neutral-500 sm:text-xs">
              {entry.content?.value || entry.summary}
            </p>
          )}
        </div>
      </Link>
    );
  }

  // Acquisition card (book) — but the entry might actually be a navigation
  // entry sitting inside a mixed feed (e.g. SBFLibrary v2's Names-letter feed
  // declared kind=acquisition has Work nodes that are pure navigation entries
  // alongside book entries). Detect by looking at the entry itself rather than
  // the page-level feed kind: no acquisition links → navigation tile.
  const imageUrl = entry.coverUrl || entry.thumbnailUrl;
  const author = entry.authors[0]?.name;
  const hasAcquisition = entry.acquisitionLinks.length > 0;
  const isNavEntry = !hasAcquisition && subsectionLink != null;
  const isFree = entry.acquisitionLinks.some((l) => l.acquisitionType === 'open-access');
  const hasFb2 = entry.acquisitionLinks.some(
    (l) => l.type?.includes('fb2') || l.indirectAcquisitions?.some((ia) => ia.type?.includes('fb2'))
  );
  const isHidden = entry.categories.some((c) => c.term === 'hidden');

  // Build click-through link:
  // 1. Nav entry inside an acquisition feed → drill into the subsection feed
  //    (Work tile, related-author link reused as primary link, etc.)
  // 2. If entry has a complete entry URL (separate OPDS entry document), use that
  // 3. Otherwise, encode the parent feed URL + entry index so we can re-fetch the feed
  let detailHref: string;
  if (isNavEntry && subsectionLink) {
    detailHref = `/library/${library.id}/feed/${encodeFeedPath(subsectionLink.href)}`;
  } else if (entry.completeEntryUrl) {
    detailHref = `/book/${library.id}/${encodeFeedPath(entry.completeEntryUrl)}`;
  } else if (feedUrl != null && entryIndex != null) {
    detailHref = `/book/${library.id}/${encodeFeedPath(feedUrl)}/${entryIndex}`;
  } else {
    detailHref = '#';
  }

  return (
    <Link
      href={detailHref}
      className={`group flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white transition-all hover:border-blue-200 hover:shadow-md ${isHidden ? 'opacity-60' : ''}`}
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-neutral-100">
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={proxyImageUrl(imageUrl, library)}
            alt={entry.title}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : isNavEntry ? (
          <NavTilePlaceholder
            title={entry.title}
            kind={classifyNavTile(entry, subsectionLink?.href)}
          />
        ) : (
          <BookCoverFallback title={entry.title} />
        )}
        {isHidden && (
          <span className="absolute right-1.5 top-1.5 rounded-md bg-neutral-900/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow">
            Скрыто
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-3">
        <h3 className="line-clamp-2 text-sm font-medium leading-tight text-neutral-900">
          {entry.title}
        </h3>
        {author && (
          <p className="mt-1 truncate text-sm text-neutral-500 sm:text-xs">{author}</p>
        )}
        <div className="mt-auto flex flex-wrap gap-1 pt-2">
          {isFree && (
            <span className="rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
              FREE
            </span>
          )}
          {hasFb2 && (
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
              FB2
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
